import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Stack} from '@mantine/core';
import React, {useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType, getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {Owner} from 'auth/PermissionConstants';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import {DataPolicyCollaborationManagementContent} from 'collaborator-components/CollaborationManagementContent';
import PoliceableDAO from 'collaborator-components/PoliceableDAO';
import useCollaborationManagementState, {
  Collaboration,
} from 'collaborator-components/useCollaborationManagementState';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getOwnerChoices, getSelectOwnerHelpMessage, OwnershipType} from 'create-components/OwnableUtil';
import SelectOwnerField from 'create-components/SelectOwnerField';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {SkillSummary, updateSkill} from 'llm/chat-library/skills/SkillsDAO';
import notifications, {Response} from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';
import {isJqXHR} from 'util/Net';

interface SkillAccessTabProps {
  skill: SkillSummary;
  onUpdate?: () => void;
}

/** Converts a collaboration party node into the Owner shape used by SelectOwnerField. */
function partyToOwner(party: Collaboration['party']): Owner {
  return {
    ...party,
    avatar_url: party.avatarUrl ?? '',
    joinedOn: '',
    url_identifier: party.id,
    type: party.__typename === 'UserNode' ? ModelTypeConstants.USER : ModelTypeConstants.ORGANIZATION,
  };
}

/**
 * Inline "Manage access" view for a skill. Collaborators are edited directly in the table; the owner can be
 * reassigned via the dropdown.
 */
export default function SkillAccessTab({skill, onUpdate}: SkillAccessTabProps): JSX.Element {
  const [collaborationState, collaborationActions] = useCollaborationManagementState(skill.id);
  const [saving, setSaving] = useState(false);
  const [canSave, setCanSave] = useState(true);

  const [pendingOwner, setPendingOwner] = useState<Owner | null>(null);

  const collaborations = collaborationState.collaborations;

  const persistedOwner = skill.owner;
  const selectedOwner = pendingOwner ?? persistedOwner;

  // Candidate owners are the global owner choices plus any party the skill is already shared with that can
  // own it: users always qualify, organizations only if they're an owner choice or the current owner. Teams
  // can never own a skill. Mirrors OwnableSettingsModal's collaboration-aware owner list.
  const potentialOwners = useMemo(() => {
    if (selectedOwner == null) {
      return [];
    }
    const ownerChoices = getOwnerChoices();
    const collaborationOwners = collaborations
      .filter(
        (c) =>
          c.party.__typename === 'UserNode' ||
          (c.party.__typename === 'OrganizationNode' &&
            (ownerChoices.some((owner) => owner.id === c.party.id) || c.fromOwnership === true))
      )
      .map((c) => partyToOwner(c.party));
    const seenOwnerIds = new Set<string>();
    return [selectedOwner, ...ownerChoices, ...collaborationOwners].filter((owner) => {
      if (seenOwnerIds.has(owner.id)) {
        return false;
      }
      seenOwnerIds.add(owner.id);
      return true;
    });
  }, [selectedOwner, collaborations]);

  const ownerChanged = pendingOwner != null && pendingOwner.id !== persistedOwner?.id;
  // isEqual does a deep comparison, so memoize it to avoid running on every render.
  const collaborationsChanged = useMemo(
    () => !isEqual(collaborations, collaborationState.initialCollaborations),
    [collaborations, collaborationState.initialCollaborations]
  );
  const hasPendingChanges = ownerChanged || collaborationsChanged;

  if (collaborationState.queryResultType === QueryResultType.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Could not load collaborators"
        supportingInfo="Try reloading the page."
      />
    );
  } else if (collaborationState.queryResultType !== QueryResultType.SUCCESS) {
    return <CenteredSpinner />;
  }

  const canManageCollaborators =
    collaborationState.policeable == null
      ? true
      : (collaborationState.policeable.permissions.canManageCollaborators ??
        collaborationState.policeable.permissions.admin);
  const readOnly = saving || !canManageCollaborators;

  function handleSelectOwner(newOwnerId: string): void {
    const newOwner = potentialOwners.find((o) => o.id === newOwnerId);
    if (newOwner != null) {
      setPendingOwner(newOwner);
    }
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    const apiBase = getApiBaseForObjectType(getObjectType({id: skill.id as CoreObjectId}));
    try {
      // Save collaborator edits before transferring ownership: while the old owner is still the owner,
      // the backend filters out their (fromOwnership) row, so the request passes validation.
      await PoliceableDAO.updateCollaborations(
        skill.id,
        convertToCollaborationCreateOrUpdates(collaborations),
        apiBase
      );
      if (ownerChanged) {
        await updateSkill(skill.id, {owner: pendingOwner.handle});
      }
      await (collaborationActions.refetch?.() ?? Promise.resolve());
      setPendingOwner(null);
      onUpdate?.();
      notifications.success('Successfully updated access.');
    } catch (response) {
      // Only server request failures are surfaced as a user-facing notification here
      if (!isJqXHR(response)) {
        throw response;
      }
      notifications.notifyError(response as Response);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack spacing="md" {...getTestProps({component: 'SkillAccessTab', element: 'root'})}>
      {selectedOwner != null && (
        <SelectOwnerField
          ownableType={ModelTypeConstants.AGENT_SKILL}
          isReadOnly={readOnly || potentialOwners.length <= 1}
          ownerId={selectedOwner.id}
          selectOwner={handleSelectOwner}
          potentialOwners={potentialOwners}
          collaboratorsAllowed={true}
          helpMessage={getSelectOwnerHelpMessage(
            ModelTypeConstants.AGENT_SKILL,
            true,
            OwnershipType.USER_AND_ORG
          )}
        />
      )}

      <DataPolicyCollaborationManagementContent
        {...collaborationState}
        {...collaborationActions}
        isReadOnly={readOnly}
        renderAuditorColumn={false}
        setCanSave={setCanSave}
      />

      {canManageCollaborators && (
        <Group position="right">
          <Button
            loading={saving}
            disabled={!canSave || saving || !hasPendingChanges}
            onClick={handleSave}
            {...getTestProps({component: 'SkillAccessTab', element: 'saveButton'})}
          >
            Save changes
          </Button>
        </Group>
      )}
    </Stack>
  );
}
